import {
  ConsentDetailItem,
  ConsentEvent,
  ConsentEventAcceptanceRaw,
  ConsentEventDocument,
  ConsentEventRaw,
  ConsentEventWithdrawal,
  ConsentHash,
  ConsentJurisdictionRaw,
  ConsentSnapshotDocumentRaw,
  ConsentSnapshotRaw,
} from '@shared/models';
import { toTitleCase } from '@shared/utils';

const CONSENT_METHOD_LABELS: Record<string, string> = {
  CHECKBOX_CLICK: 'Checkbox click',
  E_SIGNATURE: 'E-signature',
  API_ATTESTATION: 'API attestation',
  OTHER: 'Other',
};

const WITHDRAWAL_CHANNEL_LABELS: Record<string, string> = {
  PRIVACY_EMAIL: 'Privacy email',
  CONSUMER_CONSENT_CENTER: 'Consumer consent center',
  API: 'API',
};

const JURISDICTION_SOURCE_LABELS: Record<string, string> = {
  WORKFLOW: 'workflow',
  CLIENT_TENANT: 'client tenant',
};

const CONSENT_DETAIL_LABELS: Record<string, string> = {
  phoneNumber: 'Phone number',
  'verification.provider': 'Verification provider',
  'verification.reference': 'Verification reference',
  'signRequest.reference': 'Sign request',
  'signRequest.documentSha256': 'Signed document SHA-256',
  'paymentAuthorization.business': 'Business',
  'paymentAuthorization.amount': 'Amount',
  'paymentAuthorization.currency': 'Currency',
  'paymentAuthorization.frequency': 'Frequency',
  'paymentAuthorization.startDate': 'Start date',
  'paymentAuthorization.endDate': 'End date',
  'paymentAuthorization.maskedBankAccount': 'Bank account',
  'tenant.tenantId': 'Tenant ID',
  'tenant.tenantBusinessAccountId': 'Tenant business account',
  'attestation.channels': 'Attestation channels',
  'attestation.contacts': 'Attested contacts',
};

const humanizeKey = (key: string): string =>
  key
    .replace(/(?<before>[0-9a-z])(?<upper>[A-Z])/gu, '$<before> $<upper>')
    .replace(/[._]+/gu, ' ')
    .toLowerCase();

const detailLabel = (path: string[]): string => {
  const label = humanizeKey(path.join(' '));
  return CONSENT_DETAIL_LABELS[path.join('.')] ?? label.charAt(0).toUpperCase() + label.slice(1);
};

const detailValue = (value: unknown): string =>
  Array.isArray(value)
    ? value.map((item) => (item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ')
    : String(value);

const flattenDetails = (details: Record<string, unknown>, path: string[] = []): ConsentDetailItem[] =>
  Object.entries(details).flatMap(([key, value]) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return [];
    if (typeof value === 'object' && !Array.isArray(value)) return flattenDetails(value as Record<string, unknown>, [...path, key]);
    return [{ label: detailLabel([...path, key]), value: detailValue(value) }];
  });

// Versions arrive either bare ("3") or already prefixed ("v1").
const versionLabel = (version: string): string => (/^v/i.test(version) ? version : `v${version}`);

const fileHashes = (label: string, document: ConsentSnapshotDocumentRaw): ConsentHash[] =>
  (document.files ?? [])
    .filter((file) => file.sha256)
    .map((file) => ({
      label: `${label} ${versionLabel(document.version)}${file.extension ? ' (' + file.extension.toUpperCase() + ')' : ''}`,
      sha256: file.sha256!,
    }));

const hashesOf = (snapshot?: ConsentSnapshotRaw): ConsentHash[] => [
  ...(snapshot?.checkboxText?.sha256 ? [{ label: 'Checkbox text', sha256: snapshot.checkboxText.sha256 }] : []),
  ...(snapshot?.notice ? fileHashes(`Notice: ${acceptedDocumentName(snapshot.notice.documentId)}`, snapshot.notice) : []),
  ...(snapshot?.documents ?? []).flatMap((document) => fileHashes(acceptedDocumentName(document.documentId), document)),
];

export const acceptedDocumentName = (documentId: string): string =>
  toTitleCase(documentId.replace(/(?:ebt|rkor)-/iu, '').replaceAll('-', ' '));

const jurisdictionLabel = ({ code, source }: ConsentJurisdictionRaw): string => {
  const sourceLabel = source ? JURISDICTION_SOURCE_LABELS[source] : undefined;
  return sourceLabel ? `${code} (${sourceLabel})` : code;
};

// The download link names the file type it serves; the snapshot is only a fallback.
const fileExtensionOf = (downloadUrl?: string, snapshotDocument?: ConsentSnapshotDocumentRaw): string | undefined => {
  try {
    const fromUrl = downloadUrl ? new URL(downloadUrl, window.location.href).searchParams.get('fileExtension') : null;
    if (fromUrl) return fromUrl.toLowerCase();
  } catch {
    // A link that cannot be parsed falls back to the snapshot.
  }
  return snapshotDocument?.files?.find((file) => file.extension)?.extension?.toLowerCase() ?? undefined;
};

const jurisdictionsOf = (raw: ConsentEventRaw, firstRow?: ConsentEventAcceptanceRaw): string[] => {
  if (raw.jurisdictions?.length) return raw.jurisdictions.map(jurisdictionLabel);
  return firstRow?.jurisdiction ? [firstRow.jurisdiction] : [];
};

const withdrawalOf = (rows: ConsentEventAcceptanceRaw[]): ConsentEventWithdrawal => {
  const withdrawnCount = rows.filter((row) => row.withdrawnAt).length;
  if (!withdrawnCount) return 'NONE';
  return withdrawnCount === rows.length ? 'FULL' : 'PARTIAL';
};

export const mapConsentEvent = (raw: ConsentEventRaw): ConsentEvent => {
  const rows = raw.acceptances ?? [];
  const snapshot = raw.consentSnapshot ?? undefined;
  const checkboxText = snapshot?.checkboxText ?? undefined;
  const notice = snapshot?.notice ?? undefined;
  const snapshotDocuments = snapshot?.documents ?? [];
  const firstRow = rows[0];

  const documents: ConsentEventDocument[] = rows.map((row) => {
    const isCheckboxText = !!checkboxText && row.documentId === checkboxText.documentId;
    const snapshotDocument = snapshotDocuments.find(
      (document) => document.documentId === row.documentId && document.version === row.documentVersion,
    );
    const fileExtension = fileExtensionOf(row.downloadUrl, snapshotDocument);
    return {
      acceptanceId: row.id,
      documentId: row.documentId,
      documentName: acceptedDocumentName(row.documentId),
      version: row.documentVersion,
      downloadUrl: row.downloadUrl || undefined,
      fileExtension,
      financialAccountId: row.scopes?.['FINANCIAL_ACCOUNT_ID'] || undefined,
      phoneNumber: row.scopes?.['PHONE_NUMBER'] || undefined,
      workflowId: row.scopes?.['WORKFLOW_ID'] && row.scopes['WORKFLOW_ID'] !== raw.workflowId ? row.scopes['WORKFLOW_ID'] : undefined,
      withdrawnAt: row.withdrawnAt || undefined,
      withdrawalChannel: row.withdrawalChannel ? (WITHDRAWAL_CHANNEL_LABELS[row.withdrawalChannel] ?? row.withdrawalChannel) : undefined,
      isCheckboxText,
      // Acceptances recorded before consent events carry no file type, and those were always PDFs.
      canPreview: !isCheckboxText && (!fileExtension || fileExtension === 'pdf'),
    };
  });

  const jurisdictions = jurisdictionsOf(raw, firstRow);

  const details = snapshot?.details ? flattenDetails(snapshot.details) : [];

  const legacyContext =
    !raw.consentEventId && firstRow?.consentScopeSnapshot ? JSON.stringify(firstRow.consentScopeSnapshot, null, 2) : undefined;

  return {
    key: raw.consentEventId ?? firstRow?.id ?? raw.respondedAt,
    isLegacy: !raw.consentEventId,
    accepted: !!raw.accepted,
    respondedAt: raw.respondedAt,
    consentMethod: raw.consentMethod ? (CONSENT_METHOD_LABELS[raw.consentMethod] ?? raw.consentMethod) : undefined,
    ipAddress: raw.ipAddress ?? undefined,
    userAgent: raw.userAgent ?? undefined,
    workflowId: raw.workflowId ?? undefined,
    language: raw.language ?? undefined,
    jurisdictions,
    acceptorName: raw.acceptorName ?? undefined,
    acceptorEmail: raw.acceptorEmail ?? undefined,
    acceptorPhone: raw.acceptorPhone ?? undefined,
    consentDefinitionId: raw.consentDefinitionId ?? undefined,
    presentationId: raw.presentationId ?? undefined,
    placement: snapshot?.placement ? toTitleCase(snapshot.placement.replaceAll('_', ' ')) : undefined,
    checkboxText: checkboxText
      ? { text: checkboxText.text, documentId: checkboxText.documentId, version: checkboxText.version }
      : undefined,
    notice: notice
      ? { documentId: notice.documentId, documentName: acceptedDocumentName(notice.documentId), version: notice.version }
      : undefined,
    legacyContext,
    documents,
    details: details.length ? details : undefined,
    hashes: hashesOf(snapshot),
    withdrawal: withdrawalOf(rows),
  };
};
